import type { Feature } from './app-config';

import { isEmpty } from 'lodash';
import { proxy } from 'valtio';
import { devtools, subscribeKey } from 'valtio/utils';

import { FeatureItem } from '@paid-ui/enums/feature';

import { userManager } from './user';

const featureNameMap = {
  [FeatureItem.KYC_KYB]: 'enableKycKyb',
  [FeatureItem.USER_CONSENT]: 'enableUserConsent',
  [FeatureItem.WATCHMAN]: 'enableWatchman',
  [FeatureItem.CARD_PAYMENT_RBN]: 'enableCardPaymentForRBN',
  [FeatureItem.CARD_PAYMENT_SUPER_CONTRACTS]: 'enableCardPaymentForSuperContract',
  [FeatureItem.CONTRACT_FORM_NAVIGATOR]: 'enableFormDevTools',
  [FeatureItem.SECURE_CONTRACT]: 'enableSecureContractDeeds',
  [FeatureItem.SAVINGS_ACCOUNT]: 'enableSavingsAccount',
} as const;

const initialState = {
  enableKycKyb: false,
  enableUserConsent: false,
  enableWatchman: false,
  enableCardPaymentForRBN: false,
  enableCardPaymentForSuperContract: false,
  enableFormDevTools: false,
  enableSecureContractDeeds: false,
  enableSavingsAccount: false,
  features: {} as Partial<Record<FeatureItem, Feature>>,
};

export const featureManager = proxy(initialState);

devtools(featureManager, {
  name: 'Features',
  enabled: false,
});

export const saveDefaultFeatures = (features: Partial<Record<FeatureItem, Feature>>) => {
  const featureToggles = Object.values(features).reduce(
    (result, feature) => {
      if (featureNameMap[feature.name]) {
        result[featureNameMap[feature.name]] = feature.userSubscribable ? false : feature.active;
      }
      return result;
    },
    {} as typeof initialState,
  );

  const updatedFeatures = Object.fromEntries(
    Object.entries(features).map(([name, feature]) => [
      name,
      {
        ...feature,
        subscribed: feature.userSubscribable ? false : feature.active,
      },
    ]),
  );

  Object.assign(featureManager, {
    ...featureToggles,
    features: updatedFeatures,
  });
};

subscribeKey(userManager, 'features', (userFeatures) => {
  const { features } = featureManager;
  if (isEmpty(features)) return;

  const featureToggles = Object.values(features).reduce(
    (result, feature) => {
      if (featureNameMap[feature.name]) {
        result[featureNameMap[feature.name]] = feature.userSubscribable
          ? userFeatures.includes(feature.name)
          : feature.active;
      }
      return result;
    },
    {} as typeof initialState,
  );

  const updatedFeatures = Object.fromEntries(
    Object.entries(features).map(([name, feature]) => [
      name,
      {
        ...feature,
        subscribed: feature.userSubscribable ? userFeatures.includes(feature.name) : feature.active,
      },
    ]),
  );

  Object.assign(featureManager, {
    ...featureToggles,
    features: updatedFeatures,
  });
});
